import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import FitmentComponent from '../../library/fitment/fitment';
import {
  CRUMBS,
  EVENTS,
  PROP_TYPES,
  TABS
} from '../../library/fitment/fitment-constants';
import CECContactUs, {
  CECContactUsOrientation,
  CECContactUsTheme
} from '../../components/cec-contact-us/cec-contact-us';
import TreadwellDriverTypes from '../../components/treadwell-driver-types/treadwell-driver-types';
import FitmentConfirmation from './components/fitment-confirmation/fitment-confirmation';
import FitmentError from './components/fitment-error/fitment-error';
import FitmentResults from './components/fitment-results/fitment-results';
import SavedFitmentMessage from './components/saved-fitment-message/saved-fitment-message';
import ShowResultsModal from './components/show-results-modal/show-results-modal';
import CustomerServiceDownSavedFitmentsMessage from '../customer-service-down-saved-fitments-message/customer-service-down-saved-fitments-message';
import ErrorBoundary from '../error-boundary/error-boundary';
import FitmentBanner from '../fitment-banner/fitment-banner';
import TreadwellZipCodeInput from '../treadwell-zip-code-input/treadwell-zip-code-input';

import { GET_BRAND_CATEGORIES_BY_SEO_NAME } from '../../shared/graphql/category-queries';
import {
  GET_ASSEMBLIES,
  GET_FITMENT_IMAGE,
  GET_MAKES,
  GET_MODELS,
  GET_MODELS_FOR_MAKE,
  GET_OPTIONAL_SIZES,
  GET_RIDE_HEIGHTS,
  GET_TIRE_CROSS_DIMENSIONS,
  GET_TIRE_DIAMETERS,
  GET_TIRE_DIMENSIONS,
  GET_TRIMS,
  GET_WHEEL_BOLT_PATTERNS,
  GET_WHEEL_DIAMETERS,
  GET_WHEEL_WIDTHS,
  GET_YEARS,
  GET_YEARS_FOR_MAKE_AND_MODEL
} from './graphql/fitment-queries';

import {
  useFeatureFlags,
  useFitment,
  useFitmentQueries,
  useGraphQLClient,
  useMediaQuery,
  useMyAccount,
  useMyStore,
  useNavigateTo,
  usePrevious,
  useTreadwellForm
} from '../../shared/hooks/hooks';
import useLicensePlate from './hooks/use-license-plate';

import {
  getEventUIType,
  trackAction
} from '../../shared/utils/analytics/analytics';
import {
  generatePLPUrlBySize,
  getVehicleDataLayerValues,
  parseAssemblyType
} from '../../shared/utils/fitment/fitment';
import generateUID from '../../shared/utils/generate-uid/generate-uid';
import { localStorage } from '../../shared/utils/storage/storage';

import {
  TREADWELL_DRIVERTYPE_OPTION_OFFROAD,
  VEHICLE_ASSEMBLY,
  VEHICLE_MAKE,
  VEHICLE_MODEL,
  VEHICLE_RIDE_HEIGHT,
  VEHICLE_TRIM,
  VEHICLE_YEAR
} from '../../shared/constants/data-layer';
import { FEATURE_FLAGS } from '../../shared/constants/feature-flags';
import { COUNTRIES, PROVINCES, STATES } from '../../shared/constants/form';
import { PRODUCT_TYPES } from '../../shared/constants/products';
import { SEARCH_TYPES } from '../../shared/constants/search-types';
import { PLP_SLIDER_INITIAL_VALUES } from '../../shared/constants/storage-keys';

import './fitment.scss';

function Fitment(props) {
  const {
    canToggleVehicle,
    continueTo,
    hideBackButton,
    hideCecContactUs,
    hideDelete,
    hideLicensePlate,
    hideNeedAssistance,
    hideTabs,
    hideVehicleCrumbs,
    isEditing: isEditingProp,
    isLicensePlateModalEmbedded,
    isOptionsMenuOpenByDefault,
    isShopProductsCollapsible,
    isShowingRecentFitments,
    isTreadwellStart,
    isYearAfterModel,
    maxSize,
    onAddVehicleClicked,
    onBackToMyVehicles,
    onCancelFitment,
    onClose,
    onConfirmFitment,
    onEditingCompleted,
    onEvent: onEventProp,
    onLicensePlateCancel,
    onLicensePlateVehicleConfirmed,
    onSelectFitment,
    onShopProducts: onShopProductsProp,
    onSignInClick,
    onUpdateFitment,
    renderAboveFitmentSearchCtas,
    renderSelectedFitment,
    renderShopProductsButton,
    requireFitmentConfirmation: requireFitmentConfirmationProp,
    selectedBrandTab,
    selectedSizeTab,
    selectedTab,
    selectedVehicleCrumb,
    selectedVehicleDetails,
    shouldAutofocusLicensePlate,
    shouldAutofocusYear,
    shouldCallShopProducts,
    shouldDisplayShopTiresModal,
    shouldResetOnComplete,
    showShopProductsButton,
    temporaryFitment,
    textOverrides
  } = props;

  const initialTrackActionCalled = useRef(false);
  const [pendingFitment, setPendingFitment] = useState();
  const [inProgressFitment, setInProgressFitment] = useState();
  const [isFitmentConfirmed, setIsFitmentConfirmed] = useState(false);
  // TODO: Revisit the isViewingOptionalSizes state below to either use enum or other solution instead of calling it as boolean.
  const [isViewingOptionalSizes, setIsViewingOptionalSizes] = useState(false);
  const previousIsFitmentConfirmed = usePrevious(isFitmentConfirmed);
  const { isCustomerServiceDown, isLoggedIn, primaryAddress } = useMyAccount();
  const location = useLocation();
  const uiElementType = getEventUIType(location);
  const { myStore } = useMyStore();
  const [showShopByVehicleOverride, setShowShopByVehicleOverride] =
    useState(false);
  const { isMinSm } = useMediaQuery();

  const defaultCountry = COUNTRIES.find(
    c =>
      c.value ===
      (primaryAddress?.country?.isocode || myStore?.address?.country?.isocode)
  ) || { label: 'United States', value: 'US' };

  const defaultState = [...STATES, ...PROVINCES].find(
    c =>
      c.value ===
      (primaryAddress?.region?.isocode ||
        `${myStore?.address?.country?.isocode}-${myStore?.address?.region?.isocodeShort}`)
  );

  const defaultVehicleLicensePlateAddress = {
    country: defaultCountry,
    state: defaultState
  };

  useEffect(() => {
    if (isFitmentConfirmed && !previousIsFitmentConfirmed) {
      onConfirmFitment?.(pendingFitment?.isDually);
    }
  }, [
    isFitmentConfirmed,
    onConfirmFitment,
    pendingFitment,
    previousIsFitmentConfirmed
  ]);

  const client = useGraphQLClient();
  const navigateTo = useNavigateTo();
  const {
    addFitment,
    addPartialFitment,
    addRecentTireBrand,
    addRecentTireSize,
    addRecentWheelBrand,
    addRecentWheelSize,
    isLicenseFitmentCalled,
    isLicensePlateSizeSearch,
    recentFitments,
    recentTireBrands,
    recentTireSizes,
    recentWheelBrands,
    recentWheelSizes,
    removeFitment,
    removePartialFitment,
    removeTireBrand,
    removeTireSize,
    removeWheelBrand,
    removeWheelSize,
    savedFitments,
    selectFitment,
    selectedFitment,
    setIsLicenseFitmentCalled,
    setIsLicensePlateSizeSearch
  } = useFitment();
  const { getFitment } = useFitmentQueries(client);
  const { featureFlags } = useFeatureFlags();
  const isTreadwellFitmentStepsReorder = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_TREADWELL_FITMENT_STEPS_REORDER__]
  );
  const isFitmentRedesign = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_FITMENT_REDESIGN__]
  );
  const driverTypesPrimaryCTAText = isTreadwellFitmentStepsReorder
    ? 'Continue'
    : 'Show Results';
  const zipCodePrimaryCTAText = isTreadwellFitmentStepsReorder
    ? 'Show Results'
    : 'Continue';

  const [isShowResultsOpen, setIsShowResultsOpen] = useState(false);
  const [zipCodeFocus, setZipCodeFocus] = useState(false);
  const [showBackButton, setShowBackButton] = useState(
    isFitmentRedesign && selectedTab !== TABS.SHOP
  );
  const {
    isCustomDriverTypeShowing,
    isValid,
    isZipCodeValid,
    onDriverTypeSelected,
    onSelectedCustomDrivingPriorities,
    onShowPLP,
    onSubmitTreadwellPreferences,
    onToggleIsCustomDriverTypeShowing,
    onUpdateShowInStockTires,
    onUpdateShowOffRoadTires,
    onUpdateShowWinterTires,
    onZipCodeChange,
    onZipCodeKeyPress,
    selectedCustomDrivingPriorities,
    selectedCustomDrivingPrioritiesType,
    selectedDriverType,
    showInStockTires,
    showOffRoadTires,
    showWinterTires,
    zipCodeError,
    zipCodeValue,
    zipCodeWeatherData
  } = useTreadwellForm({}, selectedFitment);
  const customPrioritiesArr =
    selectedCustomDrivingPriorities?.map(p => p.id) || [];

  const { onSubmitLicensePlate } = useLicensePlate();

  const getAssemblies = useCallback(
    forTrimId =>
      client
        .query({
          query: GET_ASSEMBLIES,
          variables: {
            forTrimId
          }
        })
        .then(({ data = {} }) => data?.vehicleSelection?.assemblies),
    [client]
  );

  function onZipCodeFocus() {
    setZipCodeFocus(true);
  }

  function onZipCodeBlur() {
    setZipCodeFocus(false);
  }

  const onShopProducts = useCallback(
    fitment => {
      trackAction(
        'vehicle_modal_new_vehicle_add',
        getVehicleDataLayerValues(fitment)
      );

      onShopProductsProp?.(fitment);
    },
    [onShopProductsProp]
  );

  const getMakes = useCallback(
    forYear =>
      client
        .query({
          query: GET_MAKES,
          variables: {
            forYear: isYearAfterModel ? '' : forYear
          }
        })
        .then(({ data = {} }) => data?.vehicleSelection?.makes),
    [client, isYearAfterModel]
  );

  const getModels = useCallback(
    (forYear, forMake) => {
      if (isYearAfterModel) {
        return client
          .query({
            query: GET_MODELS_FOR_MAKE,
            variables: {
              forMake
            }
          })
          .then(({ data = {} }) =>
            data?.vehicleSelection?.models.map(model => ({
              description: model,
              id: model
            }))
          );
      }

      return client
        .query({
          query: GET_MODELS,
          variables: {
            forMake,
            forYear
          }
        })
        .then(({ data = {} }) => data?.vehicleSelection?.models);
    },
    [client, isYearAfterModel]
  );

  const getOptionalSizes = useCallback(
    ({ assemblyId, rideHeight, trimId, vehicleId }) =>
      client
        .query({
          query: GET_OPTIONAL_SIZES,
          variables: {
            assemblyId,
            rideHeight,
            trimId,
            vehicleId
          }
        })
        .then(({ data = {} }) => {
          const vehicleAssembly =
            data?.vehicleSelection?.fitment?.vehicleAssembly;
          return {
            optionalStaggeredSet: vehicleAssembly?.optionalStaggeredSet,
            tireSizes: vehicleAssembly?.tireSizes
          };
        }),
    [client]
  );

  const getRideHeights = useCallback(
    (forVehicleId, forTrimId) =>
      client
        .query({
          query: GET_RIDE_HEIGHTS,
          variables: {
            forTrimId,
            forVehicleId
          }
        })
        .then(({ data = {} }) => data?.vehicleSelection?.rideHeights),
    [client]
  );

  const getTireBrands = useCallback(() => {
    const query = GET_BRAND_CATEGORIES_BY_SEO_NAME;

    const variables = {
      onlyThatFits: false,
      productType: 'TIRES',
      seoName: 'all-brands-tires',
      storeCode: myStore?.code,
      vehicleInfo: null
    };

    return client
      .query({
        query,
        variables
      })
      .then(({ data = {} }) => data.brandCategory.bySeoName);
  }, [client, myStore]);

  const getTireCrossDimensions = useCallback(
    forWidth => {
      const width =
        typeof forWidth === 'number' ? forWidth.toString() : forWidth;
      return client
        .query({
          query: GET_TIRE_CROSS_DIMENSIONS,
          variables: {
            forWidth: width
          }
        })
        .then(({ data = {} }) => data.tireAspectRatios);
    },
    [client]
  );

  const getTireDiameters = useCallback(
    (forWidth, forAspectRatio) => {
      const width =
        typeof forWidth === 'number' ? forWidth.toString() : forWidth;

      return client
        .query({
          query: GET_TIRE_DIAMETERS,
          variables: {
            forAspectRatio,
            forWidth: width
          }
        })
        .then(({ data = {} }) => data.tireDiameters);
    },
    [client]
  );

  const getTireDimensions = useCallback(
    () =>
      client
        .query({
          query: GET_TIRE_DIMENSIONS
        })
        .then(({ data = {} }) => data),
    [client]
  );

  const getTrims = useCallback(
    forVehicleId =>
      client
        .query({
          query: GET_TRIMS,
          variables: {
            forVehicleId
          }
        })
        .then(({ data = {} }) => data?.vehicleSelection?.trims),
    [client]
  );

  const getWheelBrands = useCallback(() => {
    const query = GET_BRAND_CATEGORIES_BY_SEO_NAME;

    const variables = {
      onlyThatFits: false,
      productType: 'WHEELS',
      seoName: 'all-brands-wheels',
      storeCode: myStore?.code,
      vehicleInfo: null
    };

    return client
      .query({
        query,
        variables
      })
      .then(({ data = {} }) => data.brandCategory.bySeoName);
  }, [client, myStore]);

  const getWheelDiameters = useCallback(
    () =>
      client
        .query({ query: GET_WHEEL_DIAMETERS })
        .then(({ data = {} }) => data.wheelDiameters),
    [client]
  );

  const getWheelWidths = useCallback(
    forDiameter =>
      client
        .query({
          query: GET_WHEEL_WIDTHS,
          variables: {
            forDiameter
          }
        })
        .then(({ data = {} }) => data.wheelWidths),
    [client]
  );

  const getWheelBoltPatterns = useCallback(
    (forDiameter, forWheelWidth) =>
      client
        .query({
          query: GET_WHEEL_BOLT_PATTERNS,
          variables: {
            forDiameter,
            forWheelWidth
          }
        })
        .then(({ data = {} }) => data.boltPatterns),
    [client]
  );

  const getYears = useCallback(
    (forMake, forModel) => {
      if (isYearAfterModel) {
        return client
          .query({
            query: GET_YEARS_FOR_MAKE_AND_MODEL,
            variables: {
              forMake,
              forModel
            }
          })
          .then(({ data = {} }) => data?.vehicleSelection?.years);
      }

      return client.query({ query: GET_YEARS }).then(({ data = {} }) =>
        data?.vehicleSelection?.years?.map(year => ({
          description: year,
          id: year
        }))
      );
    },
    [client, isYearAfterModel]
  );

  function createTrackActionTireSizePayload(selectedFitment, data) {
    let payload = {
      tire_aspect_ratio: data.frontSize.crossDimension,
      tire_diameter: data.frontSize.diameter,
      tire_section_width: data.frontSize.width,
      tire_size_is_staggered: selectedFitment?.isStaggered ? '1' : '0'
    };
    if (data.rearSize.width) {
      payload = {
        ...payload,
        rear_tire_aspect_ratio: data.rearSize.crossDimension,
        rear_tire_diameter: data.rearSize.diameter,
        rear_tire_section_width: data.rearSize.width
      };
    }
    return payload;
  }
  function createTrackActionWheelSizePayload(selectedFitment, data) {
    let payload = {
      wheel_bolt_pattern: data.frontSize.boltPattern,
      wheel_diameter: data.frontSize.diameter,
      wheel_size_is_staggered: selectedFitment?.isStaggered ? '1' : '0',
      wheel_width: data.frontSize.width
    };
    if (data.rearSize.width) {
      payload = {
        ...payload,
        rear_wheel_bolt_pattern: data.rearSize.boltPattern,
        rear_wheel_diameter: data.rearSize.diameter,
        rear_wheel_width: data.rearSize.width
      };
    }
    return payload;
  }

  function trackSelectedAssembly(payload, eventIdentifier) {
    const { assembly, skipStateCheck = false } = payload;

    if (
      skipStateCheck ||
      (!skipStateCheck && !initialTrackActionCalled.current)
    ) {
      const parsedAssembly = parseAssemblyType(assembly);

      initialTrackActionCalled.current = true;
      trackAction(eventIdentifier, {
        [VEHICLE_ASSEMBLY]: {
          frontTireSize:
            parsedAssembly?.frontTireDescription || parsedAssembly?.description,
          rearTireSize: parsedAssembly?.rearTireDescription || ''
        }
      });
    }
  }

  const getFitmentImage = useCallback(
    ({ make, model, year }) =>
      client
        .query({
          query: GET_FITMENT_IMAGE,
          variables: {
            forMake: make,
            forModel: model,
            forYear: year
          }
        })
        .then(
          ({ data = {} }) =>
            data?.vehicleSelection?.vehicleDetailsForYearMakeModel
        ),
    [client]
  );

  const processPartialFitment = useCallback(async () => {
    if (
      inProgressFitment?.year &&
      inProgressFitment?.make &&
      inProgressFitment?.model
    ) {
      const fitmentImage = await getFitmentImage(inProgressFitment);
      const partialFitment = {
        ...inProgressFitment,
        largeImageUrl: fitmentImage?.vehicleLargeImage?.url,
        smallImageUrl: fitmentImage?.vehicleSmallImage?.url
      };
      await addPartialFitment(partialFitment);
    } else {
      await removePartialFitment();
    }
  }, [
    addPartialFitment,
    getFitmentImage,
    inProgressFitment,
    removePartialFitment
  ]);

  const onEvent = useCallback(
    async ({ name, payload }) => {
      if (/^ERROR_QUERY_/.test(name)) {
        return console.error('error: ', payload.message);
      }
      if (onEventProp?.({ name, payload }) === false) return;
      switch (name) {
        case EVENTS.BACK_TO_MY_VEHICLES: {
          setShowBackButton(true);
          break;
        }
        case EVENTS.BRAND_TIRE_SELECTED: {
          addRecentTireBrand({
            ...payload,
            __typename: 'FitmentBrand',
            id: generateUID()
          });
          trackAction(`shop_${uiElementType}_select_tires_by_brand`, {
            tire_brand: payload.name
          });

          localStorage(PLP_SLIDER_INITIAL_VALUES, undefined);

          await processPartialFitment();

          navigateTo(`${payload?.catalogURL}-catalog`);
          break;
        }
        case EVENTS.BRAND_TIRE_RECENT_REMOVED: {
          removeTireBrand(payload?.id);
          break;
        }
        case EVENTS.BRAND_WHEEL_SELECTED: {
          addRecentWheelBrand({
            ...payload,
            __typename: 'FitmentBrand',
            id: generateUID()
          });
          trackAction(`shop_${uiElementType}_select_wheels_by_brand`, {
            wheel_brand: payload.name
          });
          localStorage(PLP_SLIDER_INITIAL_VALUES, undefined);
          navigateTo(`${payload?.catalogURL}-catalog`);
          break;
        }
        case EVENTS.BRAND_WHEEL_RECENT_REMOVED: {
          removeWheelBrand(payload?.id);
          break;
        }
        case EVENTS.EDIT_SELECTED_FITMENT: {
          setShowBackButton(false);
          break;
        }
        case EVENTS.VEHICLE_FITMENT_LP_NEED_HELP: {
          trackAction('vehicle_license_need_help');
          break;
        }
        case EVENTS.SIZE_TIRE_NEED_HELP: {
          trackAction('shop_page_select_tires_size_need_help', {
            tire_size_select_step: payload.selectedCrumb
          });
          break;
        }
        case EVENTS.VEHICLE_FITMENT_ASSEMBLY_NEED_HELP: {
          trackAction('vehicle_select_assembly_need_help');
          break;
        }
        case EVENTS.VEHICLE_FITMENT_TRIM_NEED_HELP: {
          trackAction('vehicle_select_trim_need_help');
          break;
        }
        case EVENTS.SIZE_TIRE_SELECTED: {
          const recentTireSize = {
            __typename: 'FitmentSize',
            frontSize: {
              ...payload.frontSize,
              __typename: 'FitmentTireSize'
            },
            id: generateUID(),
            rearSize: {
              ...payload.rearSize,
              __typename: 'FitmentTireSize'
            }
          };
          trackAction(
            `shop_${uiElementType}_select_tires_size`,
            createTrackActionTireSizePayload(selectedFitment, payload)
          );
          addRecentTireSize(recentTireSize);
          localStorage(PLP_SLIDER_INITIAL_VALUES, undefined);

          await processPartialFitment();

          navigateTo(generatePLPUrlBySize(PRODUCT_TYPES.TIRES, payload));

          break;
        }
        case EVENTS.SIZE_TIRE_RECENT_REMOVED: {
          removeTireSize(payload?.id);
          break;
        }
        case EVENTS.SIZE_WHEEL_SELECTED: {
          const recentWheelSize = {
            __typename: 'FitmentSize',
            frontSize: {
              ...payload.frontSize,
              __typename: 'FitmentWheelSize'
            },
            id: generateUID(),
            rearSize: {
              ...payload.rearSize,
              __typename: 'FitmentWheelSize'
            }
          };
          trackAction(
            `shop_${uiElementType}_select_wheels_by_size`,
            createTrackActionWheelSizePayload(selectedFitment, payload)
          );
          addRecentWheelSize(recentWheelSize);
          localStorage(PLP_SLIDER_INITIAL_VALUES, undefined);
          navigateTo(generatePLPUrlBySize(PRODUCT_TYPES.WHEELS, payload));
          break;
        }
        case EVENTS.SIZE_WHEEL_RECENT_REMOVED: {
          removeWheelSize(payload?.id);
          break;
        }
        case EVENTS.VEHICLE_ADD_SELECTED: {
          setShowBackButton(false);
          setIsViewingOptionalSizes(false);
          break;
        }
        case EVENTS.VEHICLE_ASSEMBLY_SELECTED: {
          trackSelectedAssembly(payload, 'vehicle_select_assembly');
          break;
        }
        case EVENTS.VEHICLE_ASSEMBLY_OPTIONAL_TIRE_SIZE_SELECTED: {
          trackSelectedAssembly(payload, 'vehicle_select_opt_tire_size');
          break;
        }
        case EVENTS.VEHICLE_MAKE_SELECTED: {
          trackAction('vehicle_select_make', {
            [VEHICLE_MAKE]: payload
          });
          setInProgressFitment(prev => ({
            make: payload,
            year: prev?.year
          }));
          setIsViewingOptionalSizes(false);
          break;
        }
        case EVENTS.VEHICLE_MODEL_SELECTED: {
          trackAction('vehicle_select_model', {
            [VEHICLE_MODEL]: payload?.description
          });
          setInProgressFitment(prev => ({
            make: prev?.make,
            model: payload?.description,
            vehicleId: payload?.id,
            year: prev?.year
          }));
          setIsViewingOptionalSizes(false);
          break;
        }
        case EVENTS.VEHICLE_FITMENT_UPDATED: {
          setPendingFitment();
          setIsFitmentConfirmed(false);
          setInProgressFitment(null);
          await removePartialFitment();

          await addFitment(payload);
          onUpdateFitment?.();
          break;
        }
        case EVENTS.VEHICLE_FITMENT_UPDATE_PENDING: {
          setPendingFitment(payload);
          break;
        }
        case EVENTS.VEHICLE_FITMENT_SELECTED: {
          initialTrackActionCalled.current = false;
          setInProgressFitment(null);
          await removePartialFitment();
          selectFitment(payload);
          onSelectFitment?.();
          setShowBackButton(false);
          break;
        }
        case EVENTS.VEHICLE_RECENT_FITMENT_REMOVED: {
          removeFitment(payload);
          break;
        }
        case EVENTS.VEHICLE_SAVED_FITMENT_REMOVED: {
          removeFitment(payload);
          break;
        }
        case EVENTS.VEHICLE_TRIM_SELECTED: {
          initialTrackActionCalled.current = false;
          trackAction('vehicle_select_trim', {
            [VEHICLE_TRIM]: payload?.description
          });
          setInProgressFitment(prev => ({
            make: prev?.make,
            model: prev?.model,
            trim: payload?.description,
            trimId: payload?.id,
            vehicleId: prev?.vehicleId,
            year: prev?.year
          }));
          setIsViewingOptionalSizes(false);
          break;
        }
        case EVENTS.VEHICLE_RIDE_HEIGHT_SELECTED: {
          const { rideHeight, rideHeightOptions } = payload;
          if (rideHeightOptions?.length > 1) {
            trackAction('vehicle_select_ride_height', {
              [VEHICLE_RIDE_HEIGHT]: rideHeight?.description
            });
            setInProgressFitment(prev => ({
              make: prev?.make,
              model: prev?.model,
              rideHeight: rideHeight?.description,
              rideHeightType: rideHeight?.type,
              trim: payload?.description,
              trimId: prev?.trimId,
              vehicleId: prev?.vehicleId,
              year: prev?.year
            }));
          }
          break;
        }
        case EVENTS.VEHICLE_YEAR_SELECTED: {
          trackAction('vehicle_select_year', {
            [VEHICLE_YEAR]: payload?.description
          });
          setInProgressFitment({
            year: payload?.description
          });
          setIsViewingOptionalSizes(false);
          break;
        }
        case EVENTS.I_STILL_CANT_FIND_TIRE_SIZE: {
          trackAction(
            'vehicle_select_cant_find_assembly_link_click',
            getVehicleDataLayerValues(payload)
          );
          break;
        }
        case EVENTS.I_CANT_FIND_YEAR: {
          trackAction('vehicle_select_cant_find_year');
          break;
        }
        case EVENTS.I_CANT_FIND_MAKE: {
          trackAction(
            'vehicle_select_cant_find_make',
            getVehicleDataLayerValues(payload)
          );
          break;
        }
        case EVENTS.I_CANT_FIND_MODEL: {
          trackAction(
            'vehicle_select_cant_find_model',
            getVehicleDataLayerValues(payload)
          );
          break;
        }
        case EVENTS.I_CANT_FIND_TRIM: {
          trackAction(
            'vehicle_select_cant_find_trim',
            getVehicleDataLayerValues(payload)
          );
          trackAction('vehicle_select_trim_need_help');
          break;
        }
        case EVENTS.I_CANT_FIND_TIRE_SIZE_OPTION_SELECTED: {
          trackAction(
            'vehicle_select_cant_find_assembly',
            getVehicleDataLayerValues(payload)
          );
          setIsViewingOptionalSizes(false);
          break;
        }
        case EVENTS.CUSTOM_TIRE_SIZE_OPTION_SELECTED: {
          trackAction(
            'vehicle_select_custom_assembly',
            getVehicleDataLayerValues(payload)
          );
          setIsViewingOptionalSizes(false);
          break;
        }
      }
    },
    [
      onEventProp,
      addRecentTireBrand,
      uiElementType,
      processPartialFitment,
      navigateTo,
      removeTireBrand,
      addRecentWheelBrand,
      removeWheelBrand,
      selectedFitment,
      addRecentTireSize,
      removeTireSize,
      addRecentWheelSize,
      removeWheelSize,
      removePartialFitment,
      addFitment,
      onUpdateFitment,
      selectFitment,
      onSelectFitment,
      removeFitment,
      setIsViewingOptionalSizes
    ]
  );

  function onOpenShowResultsInterstitialModal() {
    setIsShowResultsOpen(true);
    onSubmitTreadwellPreferences();
  }

  function onCloseShowResultsInterstitialModal() {
    setIsShowResultsOpen(false);
    onShowPLP();
  }

  const handleOnSignInClick = useCallback(() => {
    onSignInClick?.();
    navigateTo('#/account/login');
  }, [navigateTo, onSignInClick]);

  const requireFitmentConfirmation =
    typeof requireFitmentConfirmationProp === 'function'
      ? requireFitmentConfirmationProp(pendingFitment)
      : requireFitmentConfirmationProp;
  const fitmentConfirmationText =
    typeof textOverrides?.fitmentConfirmationTextProp === 'function'
      ? textOverrides.fitmentConfirmationTextProp(pendingFitment)
      : textOverrides?.fitmentConfirmationTextProp;

  const fitmentProps = {
    canToggleVehicle,
    defaultVehicleLicensePlateAddress,
    getAssemblies,
    getFitment,
    getMakes,
    getModels,
    getOptionalSizes,
    getRideHeights,
    getTireBrands,
    getTireCrossDimensions,
    getTireDiameters,
    getTireDimensions,
    getTrims,
    getWheelBoltPatterns,
    getWheelBrands,
    getWheelDiameters,
    getWheelWidths,
    getYears,
    hideBackButton,
    hideDelete,
    hideLicensePlate,
    hideNeedAssistance,
    hideTabs,
    hideVehicleCrumbs,
    isCustomDriverTypeShowing,
    isEditing: isEditingProp,
    isFitmentConfirmed,
    isLicenseFitmentCalled,
    isLicensePlateModalEmbedded,
    isLicensePlateSizeSearch,
    isLoggedIn,
    isOptionsMenuOpenByDefault,
    isShowingRecentFitments,
    isTreadwellStart,
    isViewingOptionalSizes,
    isYearAfterModel,
    onAddVehicleClicked,
    onBackToMyVehicles,
    onEvent,
    onLicensePlateCancel,
    onLicensePlateVehicleConfirmed,
    onSelectFitment,
    onShopProducts,
    onSignInClick: handleOnSignInClick,
    onSubmitLicensePlate,
    pendingFitment,
    recentFitments,
    recentTireBrands: recentTireBrands || [],
    recentTireSizes: recentTireSizes || [],
    recentWheelBrands: recentWheelBrands || [],
    recentWheelSizes: recentWheelSizes || [],
    renderShopProductsButton,
    requireFitmentConfirmation,
    savedFitments,
    selectedBrandTab,
    selectedDriverType,
    selectedFitment,
    selectedSizeTab,
    selectedTab,
    selectedVehicleCrumb,
    selectedVehicleDetails,
    setIsLicenseFitmentCalled,
    setIsLicensePlateSizeSearch,
    setIsViewingOptionalSizes,
    setShowBackButton,
    setShowShopByVehicleOverride,
    shouldAutofocusLicensePlate,
    shouldAutofocusYear,
    shouldCallShopProducts,
    shouldResetOnComplete,
    showBackButton: isFitmentRedesign && showBackButton,
    showShopByVehicleOverride,
    showShopProductsButton,
    temporaryFitment,
    textOverrides,
    zipCodeValue
  };

  return (
    <ErrorBoundary name="Fitment" renderError={() => <FitmentError />}>
      <FitmentComponent
        {...fitmentProps}
        renderDriverTypesInput={args => (
          <TreadwellDriverTypes
            disableSubmit={!isValid}
            hideBackButton
            isCustomDriverTypeShowing={isCustomDriverTypeShowing}
            onDriverTypeSelected={onDriverTypeSelected}
            onPrimaryCTAClick={() => {
              const uiElementType = getEventUIType(location);
              trackAction(
                `shop_${uiElementType}_treadwell_${
                  isCustomDriverTypeShowing
                    ? 'legacy_entry_priorities'
                    : 'entry_persona'
                }`,
                {
                  [`${
                    isCustomDriverTypeShowing
                      ? 'tire_priorities'
                      : 'driver_type_persona'
                  }`]: isCustomDriverTypeShowing
                    ? customPrioritiesArr
                    : selectedDriverType.name,
                  [TREADWELL_DRIVERTYPE_OPTION_OFFROAD]: showOffRoadTires
                    ? '1'
                    : '0'
                }
              );
              args?.onSubmit?.();
              !isTreadwellFitmentStepsReorder &&
                onOpenShowResultsInterstitialModal();
            }}
            onSelectedCustomDrivingPriorities={
              onSelectedCustomDrivingPriorities
            }
            onToggleIsCustomDriverTypeShowing={
              onToggleIsCustomDriverTypeShowing
            }
            onUpdateShowInStockTires={onUpdateShowInStockTires}
            onUpdateShowOffRoadTires={onUpdateShowOffRoadTires}
            onUpdateShowWinterTires={onUpdateShowWinterTires}
            primaryCTAText={driverTypesPrimaryCTAText}
            selectedCustomDrivingPriorities={selectedCustomDrivingPriorities}
            selectedCustomDrivingPrioritiesType={
              selectedCustomDrivingPrioritiesType
            }
            selectedDriverType={selectedDriverType}
            showInStockTires={showInStockTires}
            showOffRoadTires={showOffRoadTires}
            showWinterTires={showWinterTires}
          />
        )}
        renderFitmentBanner={({ fitment }) => (
          <FitmentBanner fitment={fitment} />
        )}
        renderSavedFitmentsMessage={() =>
          isCustomerServiceDown ? (
            <CustomerServiceDownSavedFitmentsMessage />
          ) : (
            <SavedFitmentMessage
              isLoggedIn={isLoggedIn}
              onSignInClick={handleOnSignInClick}
            />
          )
        }
        renderSelectedFitment={args => {
          const { isEditing, onEditFitment, selectedFitment } = args;

          if (typeof renderSelectedFitment === 'function') {
            return renderSelectedFitment({ isEditing, maxSize, onEditFitment });
          }

          return (
            <div
              className="fitment-results-container"
              styleName="results-container"
            >
              {!isFitmentRedesign && (
                <FitmentBanner
                  fitment={pendingFitment || selectedFitment}
                  maxSize={maxSize}
                  onEditFitment={() => {
                    onEvent({
                      name: EVENTS.EDIT_SELECTED_FITMENT,
                      payload: selectedFitment
                    });
                    onEditFitment();
                  }}
                  showEditLink={!isEditing}
                  styleName="fitment-banner"
                />
              )}
              {!(pendingFitment && requireFitmentConfirmation) && (
                <FitmentResults
                  continueTo={continueTo}
                  fitment={selectedFitment}
                  handleStartTreadwell={() => {
                    onEditFitment(
                      isTreadwellFitmentStepsReorder
                        ? CRUMBS.DRIVING_TYPE
                        : CRUMBS.DRIVING_LOCATION
                    );
                  }}
                  headerText={textOverrides?.fitmentResultsHeader}
                  hideNeedAssistance={hideNeedAssistance}
                  isEditing={isEditingProp}
                  isShopProductsCollapsible={isShopProductsCollapsible}
                  onClose={onClose}
                  onEditingCompleted={onEditingCompleted}
                  pendingFitment={pendingFitment}
                  renderAboveFitmentSearchCtas={renderAboveFitmentSearchCtas}
                  shouldDisplayShopTiresModal={shouldDisplayShopTiresModal}
                />
              )}
              {pendingFitment && requireFitmentConfirmation && (
                <FitmentConfirmation
                  fitmentConfirmationText={fitmentConfirmationText}
                  onCancelFitment={onCancelFitment}
                  onConfirmFitment={() => {
                    setIsFitmentConfirmed(true);
                  }}
                />
              )}
            </div>
          );
        }}
        renderZipCodeInput={args => (
          <TreadwellZipCodeInput
            city={zipCodeWeatherData?.city}
            error={zipCodeError}
            isValid={isZipCodeValid}
            onBlur={onZipCodeBlur}
            onChange={onZipCodeChange}
            onFocus={onZipCodeFocus}
            onKeyPress={e => {
              if (onZipCodeKeyPress(e)) {
                args?.onSubmit?.();
                isTreadwellFitmentStepsReorder &&
                  onOpenShowResultsInterstitialModal();
              }
            }}
            onSubmit={() => {
              trackAction('pp_driver_postal_code_submit', {
                driver_postal_code: zipCodeValue,
                is_pit_pass: 1
              });
              args?.onSubmit?.();
              isTreadwellFitmentStepsReorder &&
                onOpenShowResultsInterstitialModal();
            }}
            primaryCTAText={zipCodePrimaryCTAText}
            showCityInfo={
              zipCodeFocus && zipCodeValue.length === 5 && zipCodeWeatherData
            }
            showSubmitButton
            state={zipCodeWeatherData?.state}
            value={zipCodeValue}
          />
        )}
        setShowShopByVehicleOverride={setShowShopByVehicleOverride}
        showShopByVehicleOverride={showShopByVehicleOverride}
      />
      {!hideCecContactUs && !showShopByVehicleOverride && (
        <div data-testid="cec-contact-us">
          <CECContactUs
            orientation={
              isMinSm && !isFitmentRedesign
                ? CECContactUsOrientation.ROW
                : CECContactUsOrientation.COLUMN
            }
            styleName="cec-contact-us"
            theme={CECContactUsTheme.GREY}
          />
        </div>
      )}
      {isShowResultsOpen && (
        <ShowResultsModal onClose={onCloseShowResultsInterstitialModal} />
      )}
    </ErrorBoundary>
  );
}

Fitment.propTypes = {
  canToggleVehicle: PropTypes.bool,
  continueTo: PropTypes.oneOf(Object.values(SEARCH_TYPES)),
  hideBackButton: PropTypes.bool,
  hideCecContactUs: PropTypes.bool,
  hideDelete: PropTypes.bool,
  hideLicensePlate: PropTypes.bool,
  hideNeedAssistance: PropTypes.bool,
  hideTabs: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  hideVehicleCrumbs: PropTypes.bool,
  isEditing: PropTypes.bool,
  isLicensePlateModalEmbedded: PropTypes.bool,
  isOptionsMenuOpenByDefault: PropTypes.bool,
  isShopProductsCollapsible: PropTypes.bool,
  isShowingRecentFitments: PropTypes.bool,
  isTreadwellStart: PropTypes.bool,
  isYearAfterModel: PropTypes.bool,
  maxSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  onAddVehicleClicked: PropTypes.func,
  onBackToMyVehicles: PropTypes.func,
  onCancelFitment: PropTypes.func,
  onClose: PropTypes.func,
  onConfirmFitment: PropTypes.func,
  onEditingCompleted: PropTypes.func,
  onEvent: PropTypes.func,
  onLicensePlateCancel: PropTypes.func,
  onLicensePlateVehicleConfirmed: PropTypes.func,
  onSelectFitment: PropTypes.func,
  onShopProducts: PropTypes.func,
  onSignInClick: PropTypes.func,
  onUpdateFitment: PropTypes.func,
  renderAboveFitmentSearchCtas: PropTypes.func,
  renderSelectedFitment: PropTypes.func,
  renderShopProductsButton: PropTypes.func,
  requireFitmentConfirmation: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func
  ]),
  selectedBrandTab: PropTypes.string,
  selectedSizeTab: PropTypes.string,
  selectedTab: PropTypes.string,
  selectedVehicleCrumb: PropTypes.string,
  selectedVehicleDetails: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    trimDesc: PropTypes.string,
    trimId: PropTypes.string,
    vehicleId: PropTypes.string,
    year: PropTypes.string
  }),
  shouldAutofocusLicensePlate: PropTypes.bool,
  shouldAutofocusYear: PropTypes.bool,
  shouldCallShopProducts: PropTypes.bool,
  shouldDisplayShopTiresModal: PropTypes.bool,
  shouldResetOnComplete: PropTypes.bool,
  showShopProductsButton: PropTypes.bool,
  temporaryFitment: PROP_TYPES.fitment,
  textOverrides: PropTypes.object
};

export default Fitment;
